import { Select } from '@mantine/core';
import { defineMessages, useIntl } from 'react-intl';

import { UserRole, UserShortEntityDto } from 'api/dtos/user';
import useGetCurrentEntityId from 'hooks/useGetCurrentEntityId';
import useGetUser from 'hooks/useGetUser';
import { useEventContext } from 'Screens/Dashboard/Events/EventHandler/EntityEventContext';
import styles from './EventEntityPicker.module.scss';

const canManageEvents = (entity: UserShortEntityDto) => (
    entity.role === UserRole.Owner ||
    entity.role === UserRole.Admin ||
    entity.role === UserRole.Moderator
);

const canManageGlobalEvents = (entity?: UserShortEntityDto) => Boolean(
    entity?.isParish &&
    !entity.parentId &&
    !entity.isIndependent,
);

const EventEntityPicker = () => {
    const intl = useIntl();
    const user = useGetUser();
    const currentEntityId = useGetCurrentEntityId();
    const { values, updateValues } = useEventContext();
    const selectedEntityId = values.entityId ?? currentEntityId ?? '';

    const getEntityTypeLabel = (entity: UserShortEntityDto) => {
        if (entity.isParish) {
            return intl.formatMessage(messages.parish);
        }

        if (entity.isIndependent) {
            return intl.formatMessage(messages.independent);
        }

        return intl.formatMessage(messages.parishGroup);
    };

    const selectableEntities = (user?.entities ?? []).filter(canManageEvents);
    const selectData = selectableEntities.map((entity) => ({
        value: entity.id,
        label: entity.parentName
            ? `${entity.name} (${entity.parentName}) - ${getEntityTypeLabel(entity)}`
            : `${entity.name} - ${getEntityTypeLabel(entity)}`,
    }));

    const onChange = (value: string | null) => {
        if (!value || value === selectedEntityId) {
            return;
        }

        const nextEntity = selectableEntities.find((entity) => entity.id === value);
        const nextCanManageGlobal = canManageGlobalEvents(nextEntity);

        updateValues({
            entityId: value,
            attendees: [],
            categoryIds: [],
            targetEntityIds: [],
            kind: nextCanManageGlobal ? values.kind ?? 'Local' : 'Local',
            scope: nextCanManageGlobal && values.kind === 'Global' ? values.scope : 'LocalOnly',
        });
    };

    return (
        <Select
            id="event-entity-picker"
            label={intl.formatMessage(messages.label)}
            data={selectData}
            value={selectedEntityId}
            onChange={onChange}
            disabled={values.isEdit}
            className={styles.input}
            placeholder={intl.formatMessage(messages.placeholder)}
            radius="md"
            size="md"
            searchable
            allowDeselect={false}
            comboboxProps={{ withinPortal: false }}
        />
    );
};

const messages = defineMessages({
    label: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventEntityPicker.label',
        defaultMessage: 'Grupa wydarzenia',
    },
    placeholder: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventEntityPicker.placeholder',
        defaultMessage: 'Wybierz grupę',
    },
    parish: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventEntityPicker.parish',
        defaultMessage: 'parafia',
    },
    independent: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventEntityPicker.independent',
        defaultMessage: 'niezalezża',
    },
    parishGroup: {
        id: 'src.Screens.Dashboard.Events.EventHandler.inputs.EventEntityPicker.parishGroup',
        defaultMessage: 'parafialna',
    },
});

export default EventEntityPicker;
